/*
  bf-training-onboarding.js
  Six questions, a plan reveal, and the handoff to Shopify sign in.

  Implements BullFit_Onboarding_Design (brief dated 1 October 2026). The
  design is authoritative for layout, wording, selected states and transition
  behaviour; its adaptive copy is deterministic and is ported as written.

  WHAT IS NOT PORTED: the prototype's proposal() function. It is sample design
  logic — its programme names, exercise lists and day spacing are illustrative
  and carry no reviewer approval. Everything shown on the reveal comes from the
  existing /profile and programme selection, so this adds no second
  recommendation engine and cannot release unapproved content.
*/
(function () {
  'use strict';
  var A = window.BFTrainingApp;
  if (!A) return;
  var H = A.helpers;
  var esc = H.esc, api = H.api, pub = H.pub, S = H.state, CFG = H.config;

  var DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  /* The order the questions are asked in. Routine first, because where
     someone is right now shapes how every later question reads.

     EQUIPMENT BEFORE COMFORT IS LOAD-BEARING, not a matter of taste. The
     comfort question builds its copy from the equipment answer (setupPhrase,
     below), so while comfort came third and equipment fourth the answer was
     never there yet: every new member got the bodyweight sentence whatever
     they were about to pick. Walked on a phone, same question and same member:
     "setting up each exercise" on a first run, "finding and setting up each
     machine" once equipment was set. The adaptive copy worked and was simply
     unreachable the one time it mattered.

     next-action.test.js fails if these two are swapped back. */
  var STEPS = ['routine', 'goal', 'equipment', 'comfort', 'days', 'time'];
  var KEY = 'bf_onboarding';

  /* ---- answers --------------------------------------------------------
     Held locally while signed out so the six questions work without an
     account, and written to the account on the first authenticated save. No
     defaults for a new visitor: unknown stays unknown. */
  function get() {
    try { return JSON.parse(sessionStorage.getItem(KEY) || '{}'); } catch (e) { return {}; }
  }
  function set(patch) {
    var v = get();
    Object.keys(patch).forEach(function (k) { v[k] = patch[k]; });
    try { sessionStorage.setItem(KEY, JSON.stringify(v)); } catch (e) {}
    return v;
  }
  A.__onboardingState = get;
  A.__onboardingSteps = STEPS;

  /* COMFORT DECIDES GUIDANCE, AND NOTHING ELSE.
     How much help someone wants choosing and performing exercises is not a
     statement about what their body can do or what they have done before.
     Someone who has trained for years can want pointers on an unfamiliar
     machine; someone new can be comfortable following instructions. So this
     sets the guidance level and never writes `experience`, which stays
     unknown until a member says otherwise. */
  /* THE NOUN FOLLOWS THE ANSWER THEY ALREADY GAVE.
     "Finding and setting up each machine" is the right sentence in a gym and
     the wrong one for the member who just told us they have no equipment —
     which is the whole bodyweight programme, the only programme currently
     approved. Equipment is asked before comfort, so the answer is already
     there to use — which this comment asserted for some time before it was
     true, and the order above now makes so. Same guidance either way; only
     the words change.

     The empty-equipment branch stays, because a member can reach this screen
     with equipment genuinely unanswered — the Edit and Review buttons enter at
     the first question, and nothing forces the order once someone is moving
     back and forth. Bodyweight is the safer sentence to fall back to: it is
     the only approved programme, and it asks nobody to find a machine that may
     not exist. */
  function setupPhrase() {
    var e = String((get() || {}).equipment || '').toLowerCase();
    if (!e || /bodyweight|none|no equipment/.test(e)) return 'setting up each exercise';
    /* DUMBBELLS ARE NOT MACHINES, and this used to be a two-way switch where
       everything that was not bodyweight got the machine sentence. Seen on a
       phone with Home dumbbells selected: "Walk me through finding and setting
       up each machine." There is no machine to find — the member has a pair of
       dumbbells on the floor at home. Dumbbells landed in the else branch by
       default rather than by decision.

       What dumbbells actually add over bodyweight is choosing a weight, so
       that is what the sentence offers help with. */
    if (/dumbbell/.test(e)) return 'choosing a weight and setting up each exercise';
    return 'finding and setting up each machine';
  }
  function subst(text) {
    return String(text == null ? '' : text).replace(/\{setup\}/g, setupPhrase());
  }

  var COMFORT = [
    ['guided',   'I need someone to show me',
                 'Walk me through {setup}.'],
    ['overview', 'I know the basics but appreciate pointers',
                 'Give me the session, with help a tap away.'],
    ['direct',   'I\u2019m comfortable training independently',
                 'Straight to the workout. I\u2019ll open help if I want it.']
  ];
  A.__comfortOptions = COMFORT;

  /* Kept for members whose account already records an experience level: it
     still decides their default guidance if they have never set one. The
     questionnaire no longer asks it. */
  function guidanceFor(experience) {
    if (experience === 'Experienced') return 'direct';
    if (experience === 'Some experience') return 'overview';
    return 'guided';
  }
  A.__guidanceFor = guidanceFor;
  var ROUTINE = [
    ['starting', 'Just starting', 'I have not built a regular routine yet.'],
    ['returning', 'Coming back', 'I have trained before, but took a break.'],
    ['consistent', 'Already training', 'Training is part of most weeks.']
  ];
  A.__routineOptions = ROUTINE;

  /* ONE PRIMARY OBJECTIVE. Confidence and consistency are gone from here:
     confidence is what the comfort question asks, and consistency is a habit
     rather than something a program is written to produce. */
  /* Every option is [value, label, description, art?] so the renderer never
     has to know which question it is drawing. */
  var GOALS = [
    ['Build muscle', 'Build muscle', 'Add size with resistance work I can repeat.'],
    ['Get stronger', 'Get stronger', 'Move more weight on the lifts I train.'],
    ['Lose body fat', 'Lose body fat', 'Keep muscle while I lose weight.'],
    ['Improve general fitness', 'Improve general fitness', 'Move well and feel better week to week.']
  ];

  /* Only the three families the catalogue actually has programs for. A fourth
     option with nothing behind it is a dead end the member walks into. */
  var EQUIPMENT = [
    ['Gym machines', 'Gym machines', 'A gym with plate or pin-loaded machines.', 'machines'],
    ['Home dumbbells', 'Home dumbbells', 'A pair or set of dumbbells, and room to use them.', 'dumbbells'],
    ['Bodyweight', 'Bodyweight', 'No equipment. A chair or step at most.', 'bodyweight']
  ];

  /* Simple line drawings, not photographs: they have to read at 44px on a
     phone and survive the dark background. Decorative, so the text label
     carries the meaning and these are hidden from assistive tech. */
  var EQUIP_ART = {
    machines:
      '<svg viewBox="0 0 48 48" aria-hidden="true" focusable="false">' +
      '<rect x="6" y="10" width="10" height="28" rx="2"/><rect x="9" y="14" width="4" height="5"/>' +
      '<rect x="9" y="21" width="4" height="5"/><rect x="9" y="28" width="4" height="5"/>' +
      '<path d="M20 14h8a6 6 0 0 1 6 6v6a6 6 0 0 1-6 6h-8"/><circle cx="38" cy="23" r="4"/></svg>',
    dumbbells:
      '<svg viewBox="0 0 48 48" aria-hidden="true" focusable="false">' +
      '<rect x="6" y="18" width="6" height="12" rx="1.5"/><rect x="12" y="21" width="4" height="6" rx="1"/>' +
      '<rect x="16" y="22" width="16" height="4" rx="1.5"/>' +
      '<rect x="32" y="21" width="4" height="6" rx="1"/><rect x="36" y="18" width="6" height="12" rx="1.5"/></svg>',
    bodyweight:
      '<svg viewBox="0 0 48 48" aria-hidden="true" focusable="false">' +
      '<circle cx="24" cy="11" r="4"/><path d="M24 15v12"/><path d="M24 19l-8 5M24 19l8 5"/>' +
      '<path d="M24 27l-6 10M24 27l6 10"/></svg>'
  };
  A.__goalOptions = GOALS;
  A.__equipmentOptions = EQUIPMENT;
  A.__equipmentArt = EQUIP_ART;


  /* ---- WHICH OPTIONS THE CATALOGUE CAN ACTUALLY SERVE ----------------
     The questions used to offer the same fixed options to everyone. Measured
     against the catalogue, most combinations of them selected no program at
     all: nothing ran in twenty minutes without bodyweight, nothing was
     written for an established routine without machines, no bodyweight
     program claimed muscle or strength, and nothing ran on a single day. A
     member answered honestly and reached a blank plan.

     The alternative to that is not to stretch a program over a constraint it
     does not meet. It is to stop offering an option we cannot serve, and to
     say why. So the options are asked for, keyed by the answers already
     given, and a question renders what came back.

     If the request fails we fall back to offering everything. A member who
     cannot reach the network should still be able to answer the questions;
     the plan screen is where the honest "nothing fits" lives, and it checks
     for itself. */
  /* ---- BRINGING THE ACCOUNT'S ANSWERS BACK --------------------------
     A member who answered six questions, signed in and came back on another
     device was asked all six again, with every field blank. Their answers were
     on the account the whole time; nothing ever read them back.

     Three rules decide whether the stored answers are applied.

     1. WHOSE ARE THESE? Local answers carry the customer they were given by.
        If that is not the member now signed in, they are not applied — on a
        shared device the questions would otherwise prefill with someone
        else's goal and equipment.
     2. WHICH IS NEWER? A local edit made after the account was last written
        is a deliberate change in progress, and wins. Otherwise the account
        wins. This is what keeps an edit alive while navigating between
        questions, where the account has not been written yet.
     3. ONLY WHAT WAS ASKED. availableDays fills the days question, because
        that is the question it came from. The ACCEPTED schedule is never
        used for it: agreeing to train Monday and Wednesday is not a statement
        that Friday stopped being possible. Experience is not written at all —
        it is not asked any more, and an unknown must stay unknown rather than
        being invented from guidance.
     Applied once per page, and never written back: hydrating is reading. */
  var hydrated = false;
  var ownerId = null;
  A.__onboardingOwner = function () { return ownerId; };

  function applyAccount(p, customerId) {
    var st = get();
    /* Someone else's answers are not a starting point for this member. */
    if (st.owner && customerId && String(st.owner) !== String(customerId)) {
      try { sessionStorage.removeItem(KEY); } catch (e) {}
      st = {};
    }
    var accountAt = p.reported && p.reported.updatedAt
      ? Date.parse(p.reported.updatedAt) : 0;
    var localAt = Number(st.editedAt || 0);
    var localIsNewer = localAt > 0 && accountAt > 0 && localAt > accountAt;

    var r = p.reported || {};
    var sched = p.schedule || {};
    var fromAccount = {
      routine: r.routine || null,
      goals: (r.goals && r.goals.length) ? r.goals : (r.goal ? [r.goal] : null),
      /* Guidance is what the comfort question sets. Experience is a different
         fact and is deliberately not consulted here. */
      comfort: r.guidance || null,
      equipment: r.equipment || null,
      /* AVAILABILITY, not the accepted schedule. */
      days: Array.isArray(sched.availableDays) ? sched.availableDays
          : Array.isArray(r.availableDays) ? r.availableDays : null,
      length: r.sessionLength || null
    };

    var next = {};
    Object.keys(fromAccount).forEach(function (k) {
      var stored = fromAccount[k];
      if (stored == null || (Array.isArray(stored) && !stored.length)) return;
      var local = st[k];
      var haveLocal = local != null && !(Array.isArray(local) && !local.length);
      /* A newer local edit wins; otherwise fill from the account, including
         any field the member has not touched in this browser at all. */
      if (haveLocal && localIsNewer) return;
      next[k] = stored;
    });
    next.owner = customerId || st.owner || null;
    set(next);
    return { applied: Object.keys(next), localIsNewer: localIsNewer };
  }

  /* Exposed so the screens can trigger it, and so a test can drive it. */
  A.__hydrateFromAccount = async function (force) {
    if (hydrated && !force) return { skipped: 'already hydrated' };
    if (!H.signedIn()) return { skipped: 'signed out' };
    hydrated = true;
    var me = null, prof = null;
    try { me = await api('/me'); } catch (e) {}
    try { prof = await api('/profile'); } catch (e) {}
    if (!prof || !prof.ok || !prof.data) return { skipped: 'no profile' };
    var customerId = me && me.ok && me.data ? me.data.customerId : null;
    ownerId = customerId;
    return applyAccount(prof.data, customerId);
  };

  /* THE QUESTIONS DO NOT CONSULT THE CATALOGUE.
     An earlier version asked which options the catalogue could serve and
     disabled the rest. That was the wrong trade. It meant a member who trains
     at home with dumbbells, or who is already training and has twenty
     minutes, could not say so — the only way through was to claim
     circumstances they did not have, and we would then hold a false record of
     them and plan against it.

     What we can offer is our problem, not something a member should have to
     work around. Every valid answer stays selectable, answers are recorded
     exactly as given, and a mismatch is explained at the reveal where we can
     be specific about what we do not have yet.

     A consequence worth keeping: the questions make no network calls at all,
     so nothing can re-render underneath a tap. */

  var returning = function () { return get().routine === 'returning'; };
  function guidanceLabel() {
    var g = get().comfort;
    return g === 'guided' ? 'Guided, step by step'
         : g === 'direct' ? 'Straight to the workout'
         : g === 'overview' ? 'A quick overview, help nearby'
         : 'Help whenever you want it';
  }

  /* ---- the six questions, in order, with copy that answers what came before
     ---- The adaptation is deterministic and specific: a returning member is
     asked what they want to get BACK to, someone training independently is
     told they can start directly. No repeated encouragement, no praise for
     answering a question. */
  var returningNow = function () { return get().routine === 'returning'; };
  var independent = function () { return get().comfort === 'direct'; };

  function question(step) {
    var st = get();
    var defs = [
      /* PARALLEL TO STEPS, BY POSITION. question() looks these up as
         defs[index], not by key, so this array and STEPS are one ordering
         wearing two hats — and the keys do not even match ('goals' here is
         'goal' there, 'length' is 'time'). Reorder one without the other and
         the route is right while the question is wrong: swapping STEPS alone
         rendered "What will you actually have?" under #comfort, labelled 4 of
         6. onboarding-step-order.test.js checks the two agree. */
      { key: 'routine',
        title: ['WHERE ARE YOU', 'RIGHT NOW?'],
        intro: 'Where are you with training right now?',
        options: [
          ['starting', 'Starting for the first time', 'I have not trained regularly before.'],
          ['returning', 'Returning after a break', 'I have trained before, but not lately.'],
          ['consistent', 'Training regularly', 'Training is already part of most weeks.']
        ],
        notes: {
          'starting': 'We\u2019ll look for a starting block you can repeat, not a hard first week.',
          'returning': 'Coming back is its own thing. Your starting loads matter more than what you used to lift.',
          'consistent': 'We\u2019ll fit a block around the week you already train.'
        },
        fallback: 'This decides your starting volume, not how much help you get.' },

      /* SEVERAL AT ONCE. Wanting muscle AND strength AND fat loss is the
         normal answer, not an indecisive one, and forcing a single pick threw
         away information we then planned without. */
      { key: 'goals',
        multiple: true,
        title: ['WHAT DO YOU WANT', 'TO WORK TOWARD?'],
        intro: 'Choose all that apply.',
        options: GOALS,
        notes: {
          'Build muscle': 'We\u2019ll look for resistance work you can add to over time.',
          'Get stronger': 'We\u2019ll look for a repeatable session with a clear way to progress.',
          'Lose body fat': 'Training keeps muscle while you lose weight. What you eat does the losing \u2014 we will not pretend otherwise.',
          'Improve general fitness': 'We\u2019ll look for a manageable set of movements covering the whole body.'
        },
        fallback: 'Pick as many as matter. We will tell you plainly which ones your program is written for.' },

      { key: 'equipment',
        title: ['WHAT WILL YOU', 'ACTUALLY HAVE?'],
        intro: 'Choose what you can reliably use. If nothing we have fits, we will say so rather than swap it.',
        options: EQUIPMENT,
        notes: {
          'Gym machines': 'Machine setup guidance is available on every exercise.',
          'Home dumbbells': 'Your program stays within a dumbbell pair and the space you have.',
          'Bodyweight': 'Nothing to buy. Each movement has an easier and a harder version.'
        },
        fallback: 'Pick what you will have on an ordinary day, not your best one.' },

      { key: 'comfort',
        title: ['HOW MUCH HELP', 'DO YOU WANT?'],
        intro: 'How comfortable are you choosing and performing exercises?',
        options: COMFORT,
        notes: {
          'guided': 'Your first session includes {setup} before you train.',
          'overview': 'You get the session, and exercise help is one tap away on every movement.',
          'direct': 'You can start your workout straight away, and open exercise help whenever you want it.'
        },
        fallback: 'This sets how much the app walks you through. It says nothing about what you can lift.' },

      { key: 'days',
        title: ['WHICH DAYS', 'COULD WORK?'],
        intro: 'Which days could realistically work most weeks? We propose a schedule from these \u2014 picking more days does not mean more workouts.' },

      { key: 'length',
        title: ['HOW LONG', 'IS REALISTIC?'],
        intro: returningNow()
          ? 'Give this comeback room to fit your life. How much time can you usually give one workout?'
          : 'How much time can you usually give one workout? Think about an ordinary day.',
        options: [
          ['20 minutes', '20 minutes', 'Short, and it still counts.'],
          ['30 minutes', '30 minutes', 'Enough for a full session with rest between sets.'],
          ['45 minutes', '45 minutes', 'Room for more exercises and longer rests.'],
          /* The VALUE must carry the number: minutesOf() reads the first digits it
             finds, so a stored '1 hour' would be parsed as one minute and every
             program would look too long. The label is what the member reads. */
          ['60 minutes', '1 hour', 'More than any program here asks for.']
        ],
        notes: {
          '20 minutes': 'We only offer a program that genuinely fits in twenty minutes.',
          '30 minutes': 'Most of our programs are written around this.',
          '45 minutes': 'More time is an option, not an instruction to do more.',
          '60 minutes': 'Every program we have fits well inside an hour. Choosing this does not add exercises.'
        },
        fallback: 'We will not call a longer workout a fit because you chose less time.' }
    ];
    return defs[step];
  }

  function heading(lines) {
    return '<h1 class="bf-onb-h1" id="bf-q-title" tabindex="-1">' + esc(lines[0]) +
      '<br><span class="bf-accent">' + esc(lines[1]) + '</span></h1>';
  }
  function progress(step) {
    var bars = '';
    for (var i = 0; i < 6; i++) bars += '<span class="' + (i <= step ? 'done' : '') + '"></span>';
    return '<div class="bf-onb-progress"><div class="bf-onb-progress-meta">' +
      '<span>Your starting point</span><span><b>' + (step + 1) + '</b> of 6</span></div>' +
      '<div class="bf-onb-track" role="progressbar" aria-label="Onboarding"' +
      ' aria-valuemin="1" aria-valuemax="6" aria-valuenow="' + (step + 1) + '">' + bars + '</div></div>';
  }
  /* The supporting line reserves its space whether or not an answer is
     selected, so Continue does not jump when the member chooses. */
  function supporting(text) {
    return '<p class="bf-onb-note" aria-live="polite">' +
      (text ? '<span class="bf-onb-star" aria-hidden="true">✦</span><span>' + text + '</span>' : '') +
      '</p>';
  }
  function actions(step, valid, label, help) {
    return '<div class="bf-onb-actions">' +
      (step > 0 ? '<button type="button" class="bf-onb-back" data-onb-back>' +
        '<span aria-hidden="true">←</span> Back</button>' : '') +
      '<button type="button" class="bf-onb-next" data-onb-next' + (valid ? '' : ' disabled') + '>' +
        esc(label) + ' <span aria-hidden="true">→</span></button>' +
      '</div><p class="bf-onb-help">' + esc(help) + '</p>';
  }

  function choices(q, current) {
    return '<div class="bf-onb-choices' + (q.key === 'length' ? ' bf-onb-times' : '') +
      (q.key === 'equipment' ? ' bf-onb-equip' : '') +
      '" role="' + (q.multiple ? 'group' : 'radiogroup') + '" aria-labelledby="bf-q-title">' +
      q.options.map(function (o, i) {
        var value = o[0], label = o[1], desc = o[2], art = o[3];
        /* A multiple question is a set of checkboxes, not a radio group: each
           card is independently on or off, and `current` is the chosen list. */
        var on = q.multiple
          ? (current || []).indexOf(value) > -1
          : current === value;
        /* The lead slot: a number for most questions, the minutes for time, a
           drawing for equipment. The drawing is decorative — the label carries
           the meaning, so a screen reader is not read a picture. */
        var lead = art ? (EQUIP_ART[art] || '')
          : q.key === 'length' ? String(value).replace(' minutes', '') + '<small>MIN</small>'
          : ('0' + (i + 1)).slice(-2);
        return '<button type="button" class="bf-onb-choice' + (on ? ' is-on' : '') + '"' +
          ' role="' + (q.multiple ? 'checkbox' : 'radio') + '"' +
          ' aria-checked="' + (on ? 'true' : 'false') + '"' +
          ' data-onb-key="' + esc(q.key) + '" data-onb-value="' + esc(value) + '"' +
          /* Every checkbox is tabbable; a radio group keeps one tab stop. */
          ' tabindex="' + (q.multiple ? '0'
            : (on || (!current && i === 0) ? '0' : '-1')) + '">' +
          '<span class="bf-onb-lead' + (art ? ' bf-onb-art' : '') + '" aria-hidden="true">' +
            lead + '</span>' +
          '<span class="bf-onb-copy"><span class="bf-onb-label">' + esc(label) + '</span>' +
          '<span class="bf-onb-desc">' + esc(subst(desc)) + '</span></span>' +
          /* A SET OF CHECKBOXES SHOULD NOT LOOK LIKE A SET OF RADIOS.
             "Choose all that apply" was the only thing distinguishing the
             multi-select goals step from the single-select ones — the
             indicator was the same circle in both, so three ticked goals read
             as a rendering fault rather than three choices. Square it. */
          '<span class="bf-onb-check' + (q.multiple ? ' is-multi' : '') +
          '" aria-hidden="true">\u2713</span></button>';
      }).join('') + '</div>';
  }

  function dayPicker(chosen) {
    var picked = DAYS.filter(function (d) { return chosen.indexOf(d) > -1; });
    return '<div class="bf-onb-days" role="group" aria-label="Available training days">' +
      DAYS.map(function (d) {
        var on = chosen.indexOf(d) > -1;
        return '<button type="button" class="bf-onb-day' + (on ? ' is-on' : '') + '"' +
          ' data-onb-day="' + d + '" aria-pressed="' + (on ? 'true' : 'false') + '">' + d +
          '<span class="bf-onb-check" aria-hidden="true">✓</span></button>';
      }).join('') + '</div>' +
      '<div class="bf-onb-avail"><div class="bf-onb-avail-title">' +
        (picked.length ? picked.length + (picked.length === 1 ? ' day available' : ' days available')
                       : 'Choose the days that could work') + '</div>' +
        '<p>' + (picked.length ? esc(picked.join(' · '))
                               : 'Tap a day to select it. Tap again to remove it.') + '</p></div>' +
      supporting('Available days are room to work with. <strong>They are not a commitment to train every day.</strong>');
  }

  function questionScreen(step) {
    var q = question(step), st = get();
    /* VALID MEANS ANSWERED. Whether the catalogue can serve that answer is
       not a reason to hold someone on a question — it is something to say at
       the reveal. */
    var valid = q.key === 'days' ? (st.days || []).length > 0
              : q.multiple ? (st[q.key] || []).length > 0
              : !!st[q.key];
    var body = q.key === 'days'
      ? dayPicker(st.days || [])
      : choices(q, st[q.key]) + supporting(esc(
          /* With several chosen, the note for the most recent one is the
             useful line; the fallback covers none chosen. */
          subst((q.multiple
            ? q.notes[(st[q.key] || [])[(st[q.key] || []).length - 1]]
            : q.notes[st[q.key]]) || q.fallback)));
    return '<section class="bf-onb-q">' + progress(step) + heading(q.title) +
      '<p class="bf-onb-intro">' + esc(q.intro) + '</p>' + body +
      actions(step, valid,
        step === 5 ? 'See my starting plan' : 'Continue',
        step === 5 ? 'No payment anywhere in this questionnaire.'
                   : 'You can go back and change any answer.') +
      '</section>';
  }

  for (var i = 0; i < STEPS.length; i++) {
    (function (n) {
      A.register(STEPS[n], async function () {
        /* Read the account before drawing the first question, so a member who
           already answered sees their answers rather than six blanks. It runs
           once per page and is a no-op when signed out. */
        try { await A.__hydrateFromAccount(); } catch (e) {}
        return questionScreen(n);
      });
    })(i);
  }

  /* ---- saving ---------------------------------------------------------
     Signed out, the answers stay local. Signed in, they go to the account —
     and an established guidance preference is not overwritten just because
     the member edited another answer. */
  async function persist(extra) {
    if (!H.signedIn()) return { ok: false, reason: 'signed_out' };
    var st = get(), tz = null;
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch (e) {}
    var body = {
      goals: (st.goals && st.goals.length) ? st.goals : null,
      equipment: st.equipment || null,
      routine: st.routine || null, sessionLength: st.length || null, timezone: tz
    };
    if ((st.days || []).length) body.availableDays = st.days;

    /* THE COMFORT ANSWER IS AN EXPLICIT GUIDANCE CHOICE, so it is sent as one.
       EXPERIENCE IS NOT SENT AT ALL. Nothing in this questionnaire asks what
       someone has done before, and inferring it from how much help they want
       would be exactly the conversion we are told not to make — a member who
       asks to be shown a machine has not told us they are a beginner. An
       account that already records an experience level keeps it untouched. */
    if (st.comfort) body.guidance = st.comfort;

    Object.keys(extra || {}).forEach(function (k) { body[k] = extra[k]; });
    var r = await api('/preferences', { method: 'POST', body: body });
    if (r.ok) { S.preferences = null; S.program = null; S.profile = null; }
    return r;
  }
  A.__persistPreferences = persist;

  /* WHICH QUESTIONS ARE STILL BLANK, in the order they are asked.
     STEPS holds route names and the answers are keyed by field name; the two
     lists are parallel and must stay that way, which is what the test asserts. */
  var FIELD_OF = { routine: 'routine', goal: 'goals', comfort: 'comfort',
                   equipment: 'equipment', days: 'days', time: 'length' };
  function answered(st, field) {
    var v = st[field];
    return v != null && v !== '' && !(Array.isArray(v) && !v.length);
  }
  A.__answeredCount = function () {
    var st = get();
    return STEPS.filter(function (r) { return answered(st, FIELD_OF[r]); }).length;
  };
  /* The first question with no answer, or null when all six are answered. */
  A.__firstUnanswered = function () {
    var st = get();
    for (var i = 0; i < STEPS.length; i++) {
      if (!answered(st, FIELD_OF[STEPS[i]])) return STEPS[i];
    }
    return null;
  };

  /* RESUME IS NOT THE SAME DOOR AS "EDIT MY ANSWERS".
     Both used to be '#routine', so a member returning with every answer
     already on their account was walked through six questions they had
     answered to reach the one thing they came for. This route reads the
     account first and then sends them to the first blank question, or to the
     reveal when nothing is blank. '#routine' still means "start at the
     beginning", which is what the Edit and Review buttons want. */
  A.register('resume', async function () {
    try { await A.__hydrateFromAccount(); } catch (e) {}
    var next = A.__firstUnanswered();
    location.replace(location.pathname + location.search + '#' + (next || 'plan'));
    return '';
  });

  A.__onboardingComplete = function () {
    var st = get();
    return !!(st.routine && (st.goals || []).length && st.comfort && st.equipment &&
              (st.days || []).length && st.length);
  };

  A.register('plan', async function () { return A.__planReveal(); });
  A.register('save', async function () { return A.__saveHandoff(); });

  window.__bfOnboardingReady = true;
})();
